/**
 * persona-i18n.ts — Lot 8 i18n (28/05/2026)
 *
 * Helper unique pour résoudre un champ texte libre de persona selon la langue
 * active. Évite de répéter la cascade <field>Fr / <field>En / legacy dans
 * chaque composant.
 *
 * Champs supportés : "function", "painPoints", "personalMotivations",
 * "dailyIrritants", "typicalObjections" (champs texte libre traduits — cf.
 * persona-translator.ts).
 *
 * Cascade (snake_case et camelCase acceptés en entrée, comme les personae bruts
 * d'API) :
 *   - lang "fr" → <field>Fr || <field>En || <field> (legacy) || ""
 *   - autre     → <field>En || <field>Fr || <field> (legacy) || ""
 *
 * Le champ legacy est conservé en dernier recours pour les personae pas encore
 * migrés / pas encore retraduits par le cron.
 */

type PersonaTranslatableField =
  | "function"
  | "painPoints"
  | "personalMotivations"
  | "dailyIrritants"
  | "typicalObjections"
  | "neverDisclose"
  | "decisionMakerProfile";

// Mapping champ logique → noms de propriétés legacy / FR / EN (camelCase +
// snake_case pour tolérer les personae bruts venant de l'API).
const FIELD_KEYS: Record<
  PersonaTranslatableField,
  { legacy: string[]; fr: string[]; en: string[] }
> = {
  function: {
    legacy: ["function"],
    fr: ["functionFr", "function_fr"],
    en: ["functionEn", "function_en"],
  },
  painPoints: {
    legacy: ["painPoints", "pain_points"],
    fr: ["painPointsFr", "pain_points_fr"],
    en: ["painPointsEn", "pain_points_en"],
  },
  personalMotivations: {
    legacy: ["personalMotivations", "personal_motivations"],
    fr: ["personalMotivationsFr", "personal_motivations_fr"],
    en: ["personalMotivationsEn", "personal_motivations_en"],
  },
  dailyIrritants: {
    legacy: ["dailyIrritants", "daily_irritants"],
    fr: ["dailyIrritantsFr", "daily_irritants_fr"],
    en: ["dailyIrritantsEn", "daily_irritants_en"],
  },
  typicalObjections: {
    legacy: ["typicalObjections", "typical_objections"],
    fr: ["typicalObjectionsFr", "typical_objections_fr"],
    en: ["typicalObjectionsEn", "typical_objections_en"],
  },
  neverDisclose: {
    legacy: ["neverDisclose", "never_disclose"],
    fr: ["neverDiscloseFr", "never_disclose_fr"],
    en: ["neverDiscloseEn", "never_disclose_en"],
  },
  decisionMakerProfile: {
    legacy: ["decisionMakerProfile", "decision_maker_profile"],
    fr: ["decisionMakerProfileFr", "decision_maker_profile_fr"],
    en: ["decisionMakerProfileEn", "decision_maker_profile_en"],
  },
};

function firstFilled(obj: Record<string, any>, keys: string[]): string {
  for (const k of keys) {
    const v = obj?.[k];
    if (typeof v === "string" && v.trim()) return v;
  }
  return "";
}

export function pickPersonaField(
  persona: Record<string, any> | null | undefined,
  field: PersonaTranslatableField,
  language: string | undefined,
): string {
  if (!persona) return "";
  const keys = FIELD_KEYS[field];
  // Garde défensive : un champ non mappé ne doit jamais faire planter le rendu
  // (mieux vaut une chaîne vide qu'un crash de toute la page).
  if (!keys) return firstFilled(persona, [field]);
  const fr = firstFilled(persona, keys.fr);
  const en = firstFilled(persona, keys.en);
  const legacy = firstFilled(persona, keys.legacy);
  const isFr = (language || "fr").toLowerCase().startsWith("fr");
  if (isFr) {
    return fr || en || legacy || "";
  }
  return en || fr || legacy || "";
}
